import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapView from 'react-native-map-clustering';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { BackHandler, Linking, useColorScheme } from 'react-native';
import { useTheme } from 'styled-components/native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { version } from '../../../package.json';

import {
  Container,
  Form,
  Map,
  MapHeader,
  NeastedStationTitle,
  ButtonsCategory,
  ButtonCategory,
  ButtonCategoryText,
  FiltersContainer,
  LocationPill,
} from './styles';

import axios from 'axios';
import { api } from '../../services/api';
import { computeNearStation } from './utils';

import { StationDTO } from '../../dtos/StationDTO';

import { Loading } from '../../components/Interface/Loadings/Loading';
import { SearchInput } from '../../components/Interface/Forms/Inputs/SearchInput';

import { useLocalization } from '../../context/localization';
import { FocusAwareStatusBar } from '../../context/statusbar';
import { PromoModal } from '../../components/Interface/PromoModal';
import { ScrollView } from 'react-native-gesture-handler';

import { CouponDTO } from '../../dtos/CouponDTO';
import { StationMarker } from '../../components/shared/StationMarker';
import { LinkButton } from '../../components/Interface/Forms/Buttons/LinkButton';
import { getPersistentDeviceId } from '../../utils/deviceId';
import * as geolib from 'geolib';
import { ButtonIcon, IconHeader } from '../../components/Interface/Headers/Header/styles';

export interface ICategory {
  nome: string;
  codigo: string;
}


export function StationsMap() {
  const isFocused = useIsFocused();
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const { position, geolocation, isLocationLoading } = useLocalization();
  const navigation = useNavigation();

  // ── Refs ───────────────────────────────────────────────────
  const mapRef = useRef<MapView>(null);
  const scrollRef = useRef<ScrollView>(null);
  const regionChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Estado: localização ────────────────────────────────────
  const [selectedCity, setSelectedCity] = useState<string | null>(
    geolocation?.cidade || null,
  );
  const [selectedState, setSelectedState] = useState<string | null>(
    geolocation?.uf || null,
  );
  const [selectionSource, setSelectionSource] = useState<'geo' | 'manual'>(
    geolocation?.cidade ? 'geo' : 'manual',
  );

  // ── Estado: dados ──────────────────────────────────────────
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [data, setData] = useState<StationDTO[]>([]);
  const [search, setSearch] = useState<StationDTO[]>([]);

  // ── Estado: UI ─────────────────────────────────────────────
  const [selectedCategories, setSelectedCategories] = useState('C');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [showNoStationsModal, setShowNoStationsModal] = useState(false);
  const [isNationalView, setIsNationalView] = useState(false);

  const nearStation = useMemo(
    () => computeNearStation(data, position),
    [data, position],
  );

  const themedStyles = useMemo(
    () => ({
      shadow: {
        shadowColor: theme.colors.text_dark,
        shadowOffset: { width: 0, height: 2 } as const,
        shadowOpacity: 0.1,
        shadowRadius: 6,
        elevation: 8,
      },
    }),
    [theme],
  );

  function handleGoBack() {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Initial');
    }
  }

  async function fetchStations(
    city: string | null,
    type: string,
    signal: AbortSignal,
  ): Promise<void> {
    setIsLoading(true);
    try {
      const url = city
        ? `/stations?tipoProduto=${type}&cidade=${city}`
        : `/stations?tipoProduto=${type}`;
      const response = await api.get(url, {
        cancelToken: new axios.CancelToken((cancel: any) => {
          signal.addEventListener('abort', () => cancel('Request aborted'));
        }),
      });
      const stations: StationDTO[] = response.data;

      if (stations.length > 0) {
        setData(stations);
        setShowNoStationsModal(false);
      } else {
        setData([]);
        setShowNoStationsModal(true);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }
      console.error('Erro ao buscar estações:', err);
    } finally {
      setIsLoading(false);
    }
  }

  // Busca categorias uma vez no mount
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response = await api.post('/tipoProduto');
        if (response.data) {
          const filtered = response.data.filter(
            (item: any) => item.codigo !== 'M' && item.codigo !== 'B',
          );
          setCategories(filtered);
        }
      } catch (err) {
        console.error('Erro ao buscar categorias:', err);
      }
    }
    fetchCategories();
  }, []);

  // Busca postos sempre que cidade, categoria ou modo de visualização mudar.
  // NACIONAL: ignora filtro de cidade (usuário fez zoom out além de 1.0 delta).
  // CIDADE: filtra pela cidade selecionada (ou todos se não houver cidade).
  useEffect(() => {
    if (isLocationLoading) {
      return;
    }
    const controller = new AbortController();
    const cityParam = isNationalView ? null : selectedCity;
    fetchStations(cityParam, selectedCategories, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity, selectedCategories, isLocationLoading, isNationalView]);

  // Ajusta o zoom sempre que os dados mudam (cidade/categoria/modo) ou
  // quando a tela volta ao foco — o <Map> remonta via {isFocused && <Map>}
  // e precisa reposicionar para a cidade selecionada, não para o GPS do usuário.
  // Três casos:
  //   1. Sem GPS ou cidade diferente da localização: anima para o centroide dos postos
  //   2. Com GPS e postos próximos: bounding box usuário + 3 postos mais próximos
  //   3. Com GPS mas sem postos em 5km (cidade diferente): centroide dos postos
  useEffect(() => {
    if (!isFocused || data.length === 0 || isNationalView) {
      return;
    }

    // Pequeno delay para garantir que o mapRef está disponível após o Map remontar
    const timer = setTimeout(() => {
      if (!mapRef.current) {
        return;
      }

      const animateToCentroid = () => {
        const centerLat = data.reduce((sum, s) => sum + s.lat, 0) / data.length;
        const centerLng = data.reduce((sum, s) => sum + s.lng, 0) / data.length;
        (mapRef.current as any)?.animateToRegion(
          { latitude: centerLat, longitude: centerLng, latitudeDelta: 0.09, longitudeDelta: 0.09 },
          600,
        );
      };

      if (!position) {
        // Sem GPS — centraliza na cidade selecionada manualmente
        animateToCentroid();
        return;
      }

      const NEARBY_RADIUS_M = 10000;
      const userCoords = { latitude: position.latitude, longitude: position.longitude };

      const nearby = [...data]
        .map(s => ({
          station: s,
          dist: geolib.getDistance(userCoords, { latitude: s.lat, longitude: s.lng }),
        }))
        .filter(s => s.dist <= NEARBY_RADIUS_M)
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 3)
        .map(s => s.station);

      if (nearby.length === 0) {
        // GPS disponível mas nenhum posto em 5km — usuário selecionou cidade diferente
        animateToCentroid();
        return;
      }

      // Postos próximos encontrados — bounding box usuário + 3 mais próximos
      const allPoints = [
        userCoords,
        ...nearby.map(s => ({ latitude: s.lat, longitude: s.lng })),
      ];

      const minLat = Math.min(...allPoints.map(p => p.latitude));
      const maxLat = Math.max(...allPoints.map(p => p.latitude));
      const minLng = Math.min(...allPoints.map(p => p.longitude));
      const maxLng = Math.max(...allPoints.map(p => p.longitude));

      const latDelta = Math.max((maxLat - minLat) * 1.5, 0.01);
      const lngDelta = Math.max((maxLng - minLng) * 1.5, 0.01);

      (mapRef.current as any)?.animateToRegion(
        {
          latitude: (maxLat + minLat) / 2,
          longitude: (maxLng + minLng) / 2,
          latitudeDelta: latDelta,
          longitudeDelta: lngDelta,
        },
        600,
      );
    }, 150);

    return () => clearTimeout(timer);
  }, [data, isFocused]);

  useEffect(() => {
    if (!selectedState && geolocation?.uf) {
      setSelectedState(geolocation.uf);
    }
  }, [geolocation?.uf, selectedState]);

  useEffect(() => {
    if (!selectedCity && geolocation?.cidade) {
      setSelectedCity(geolocation.cidade);
      setSelectionSource('geo');
    }
  }, [geolocation?.cidade, selectedCity]);

  useEffect(() => {
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => false,
    );
    return () => subscription.remove();
  }, []);

  function searchRegions(name: string) {
    const term = name.toLowerCase().normalize('NFD');
    const filter = data.filter(
      (e: StationDTO) =>
        e.neighbourhood.toLowerCase().normalize('NFD').includes(term) ||
        e.city.toLowerCase().normalize('NFD').includes(term) ||
        e.flag.toLowerCase().normalize('NFD').includes(term) ||
        e.name.toLowerCase().normalize('NFD').includes(term),
    );
    setSearch(filter);
    setSearchTerm(name);
  }

  const handleCategoryPress = (category: string, index: number) => {
    setSelectedCategories(category);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ x: index * 135, animated: true });
    }
  };

  const handleStation = useCallback(
    async (tipo: string, stationData: StationDTO, coupon?: CouponDTO) => {
      const station = stationData;
      setIsLoadingOrder(true);

      const deviceId = await getPersistentDeviceId();

      await api
        .get(
          '/fuels?tipo=' +
          tipo +
          '&regiao=' +
          station.state +
          '&versao=' +
          version +
          '&deviceId=' +
          deviceId,
        )
        .then(response => {
          const fuelsData = response.data;

          if (tipo) {
            if (tipo === 'C' && fuelsData) {
              const sortedProducts = fuelsData
                .sort(function (a: any, b: any) {
                  function value(el: number) {
                    return el === 0 || el === null ? Infinity : el;
                  }
                  return value(a.ordem) - value(b.ordem);
                })
                .filter((item: any) => item.region === station.state);

              return navigation.navigate('Fuel', {
                localidade: {
                  cidade: station.cidade,
                  estado: station.localidade,
                  rank: station.categoria,
                  region: station.state,
                  tipo,
                },
                posto: station,
                fuels: sortedProducts,
                coupon,
              });
            }

            navigation.navigate('Products', {
              localidade: station.state,
              tipo,
              categoria: station.categoria,
              cidade: station.cidade,
              estado: station.localidade,
              posto: station,
              coupon,
            });
          } else {
            navigation.navigate('Station', { item: station });
          }
        })
        .catch(err => {
          console.error('Erro ao buscar combustíveis:', err);
        })
        .finally(() => {
          setIsLoadingOrder(false);
        });
    },
    [navigation],
  );

  const handleMarkerSelect = useCallback(
    (markerId: string) => {
      setSelectedMarkerId(markerId);
      if (markerId === nearStation?.id && nearStation) {
        (mapRef.current as any)?.animateToRegion(
          {
            latitude: nearStation.lat,
            longitude: nearStation.lng,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
          },
          400,
        );
      }
    },
    [nearStation],
  );

  const handleCityOrTypeChange = (newCity: string | null, newType: string) => {
    setSelectedCategories(newType);
    setSearchTerm('');
    setSearch([]);
    setIsNationalView(false); // Seleção manual sempre volta ao modo CIDADE
    if (newCity) {
      setSelectedCity(newCity);
    }
  };

  const handleSupportWhatsApp = () => {
    const city = selectedCity || geolocation?.cidade || 'minha cidade';
    const state = selectedState || geolocation?.uf;
    const locationLabel = state ? `${city} - ${state}` : city;
    const message = `Olá! Não encontrei postos na cidade ${locationLabel} pelo app. Podem me ajudar?`;
    const whatsappUrl = `https://api.whatsapp.com/send/?phone=5561998202004&text=${encodeURIComponent(
      message,
    )}&type=phone_number&app_absent=0`;
    Linking.openURL(whatsappUrl);
    setShowNoStationsModal(false);
  };

  return (
    <Container>
      {isLoading && <Loading />}
      <FocusAwareStatusBar
        barStyle={colorScheme === 'light' ? 'dark-content' : 'light-content'}
        backgroundColor={theme.colors.primary}
      />
      <MapHeader>
        <SafeAreaView>
          <ButtonIcon
            onPress={handleGoBack}
            activeOpacity={1}>
            <IconHeader
              name="arrow-left"
            />
          </ButtonIcon>
          <Form>
            <SearchInput
              placeholder="Buscar bairro, posto ou bandeira"
              value={searchTerm}
              onChangeText={name => searchRegions(name)}
              shadow
            />
            {categories.length > 0 && (
              <FiltersContainer>
                <ButtonsCategory ref={scrollRef}>
                  {categories.map((category, index) => (
                    <ButtonCategory
                      style={themedStyles.shadow}
                      activeOpacity={1}
                      key={category.codigo}
                      selected={selectedCategories === category.codigo}
                      onPress={() =>
                        handleCategoryPress(category.codigo, index)
                      }>
                      <ButtonCategoryText
                        selected={selectedCategories === category.codigo}>
                        {category.nome}
                      </ButtonCategoryText>
                    </ButtonCategory>
                  ))}
                </ButtonsCategory>
              </FiltersContainer>
            )}
          </Form>

          <LocationPill style={themedStyles.shadow}>
            <NeastedStationTitle>
              {selectedCity
                ? selectedCity.length > 18
                  ? selectedCity.substring(0, 18) + '...'
                  : selectedCity
                : 'Todos os postos'}
            </NeastedStationTitle>

            <LinkButton
              text={selectedCity ? 'Trocar' : 'Filtrar por cidade'}
              action={() =>
                navigation.navigate('MapLocation', {
                  tipo: selectedCategories,
                  callback: (payload: { city: string; state: string }) => {
                    setSelectedState(payload.state);
                    setSelectionSource('manual');
                    handleCityOrTypeChange(payload.city, selectedCategories);
                  },
                })
              }
              enabled
            />
          </LocationPill>
        </SafeAreaView>
      </MapHeader>
      {isFocused && (
        <Map
          initialRegion={{
            latitude: position?.latitude ?? -14.2895671,
            longitude: position?.longitude ?? -51.5056385,
            latitudeDelta: position ? 0.09 : 40,
            longitudeDelta: position ? 0.09 : 40,
          }}
          ref={mapRef}
          clusteringEnabled
          radius={50}
          minZoom={10}
          maxZoom={17}
          clusterColor={theme.colors.primary}
          showsUserLocation
          mapPadding={{ top: 0, right: 12, left: 12, bottom: 0 }}
          onRegionChangeComplete={(region: any) => {
            const national = region.latitudeDelta > 1.0;
            if (regionChangeTimeoutRef.current) {
              clearTimeout(regionChangeTimeoutRef.current);
            }
            regionChangeTimeoutRef.current = setTimeout(() => {
              setIsNationalView(national);
            }, 800);
          }}
          onPress={(event: any) => {
            if (event.nativeEvent.action !== 'marker-press') {
              setSelectedMarkerId(null);
            }
          }}
          customMapStyle={theme.mapstyle}>
          {searchTerm.length >= 1 &&
            search.map(marker => (
              <StationMarker
                key={marker.id}
                marker={marker}
                theme={theme}
                isSelected={marker.id === selectedMarkerId}
                isNearest={marker.id === nearStation?.id}
                onSelect={() => handleMarkerSelect(marker.id)}
                position={position as any}
                selectedCategories={selectedCategories}
                isLoadingOrder={isLoadingOrder}
                handleStation={handleStation}
              />
            ))}
          {searchTerm.length <= 0 &&
            data.map(marker => (
              <StationMarker
                key={marker.id}
                marker={marker}
                theme={theme}
                isSelected={marker.id === selectedMarkerId}
                isNearest={marker.id === nearStation?.id}
                onSelect={() => handleMarkerSelect(marker.id)}
                position={position as any}
                selectedCategories={selectedCategories}
                isLoadingOrder={isLoadingOrder}
                handleStation={handleStation}
              />
            ))}
        </Map>
      )}
      <PromoModal
        isVisible={showNoStationsModal}
        headline="Nenhum posto encontrado"
        text={
          'Não encontramos postos disponíveis nesta região. Tente procurar em outra localidade ou fale com nosso suporte para que possamos te ajudar.'
        }
        buttonText={'Escolher outra localidade'}
        buttonAction={() => {
          setShowNoStationsModal(false);
          navigation.navigate('MapLocation', {
            tipo: selectedCategories,
            callback: (payload: { city: string; state: string }) => {
              setSelectedState(payload.state);
              setSelectionSource('manual');
              handleCityOrTypeChange(payload.city, selectedCategories);
            },
          });
        }}
        tertiaryButtonText={
          selectionSource === 'geo'
            ? undefined
            : 'Falar com suporte no WhatsApp'
        }
        tertiaryButtonAction={
          selectionSource === 'geo' ? undefined : handleSupportWhatsApp
        }
        onDismiss={() => setShowNoStationsModal(false)}
      />
    </Container>
  );
}
